/*
 * Copyright 2022 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * External dependencies
 */
import * as React from 'react';
import { useState, useCallback, useMemo, useRef } from '@web-stories-wp/react';
import type { PropsWithChildren } from 'react';
import type {
  CustomFontData,
  Page,
} from '@src/web-stories-wp/story-editor/src/elements/types';

/**
 * Internal dependencies
 */
import useIdleQueue from '../../utils/useIdleTaskQueue';
import { useStory } from '../story';
import { useLutFilters } from '../lutFilters';
import { STABLE_ARRAY } from '../../constants';
import storyPageToCanvas from './utils/storyPageToCanvas';
import Context from './context';
import usePageCanvasMap from './usePageCanvasMap';
import PageCanvasCacheValidator from './pageCanvasCacheValidator';
import { getDownloadedFontCSS } from '../../utils/getFontCSS';
import { getFontMetricOverrides } from '../../elements/text/utils/selectFontMetrics';
import { hasPendingPageLutAssets } from './utils/lutPreview';

/**
 * @typedef {import('@googleforcreators/elements').Page} Page
 */

function PageCanvasProvider({ children }: PropsWithChildren<unknown>) {
  const [fontsCss, setFontsCss] = useState<Record<string, string>>({});
  const queueIdleTask = useIdleQueue();
  // This is our cache to hold generated canvases for
  // full story pages within the story we're viewing
  const [pageCanvasMap, actions] = usePageCanvasMap();
  const lutAssetsByVersionId = useLutFilters(
    ({ state }) => state.assetsByVersionId
  );
  const lutResolutionError = useLutFilters(
    ({ state }) => state.resolutionError
  );
  const lutContext = useMemo(
    () => ({
      state: {
        assetsByVersionId: lutAssetsByVersionId,
        resolutionError: lutResolutionError,
      },
    }),
    [lutAssetsByVersionId, lutResolutionError]
  );

  const pageIds = useStory(({ state }) => state.pages.map(({ id }) => id));
  const { currentPage, singleElementSelection } = useStory(({ state }) => ({
    singleElementSelection:
      state.selectedElementIds?.length === 1
        ? state.selectedElementIds
        : STABLE_ARRAY,
    currentPage: state.currentPage,
  }));

  // sync values to a ref so callbacks don't cause re-renders
  // on consuming components
  const values = {
    currentPageValue: currentPage as Page,
    pageCanvasMapValue: pageCanvasMap,
    singleElementSelectionValue: singleElementSelection,
  };
  const valuesRef = useRef(values);
  // eslint-disable-next-line react-hooks/refs -- FIXME
  valuesRef.current = values;

  const getFontCss = useCallback(
    async (
      fontData: CustomFontData,
      cacheKey: string
    ): Promise<string | null> => {
      const existingFontCss = fontsCss[cacheKey];

      if (existingFontCss) {
        return existingFontCss;
      }

      const downloadedFontCss = await getDownloadedFontCSS(
        fontData.family,
        fontData.url,
        fontData.urls,
        getFontMetricOverrides(fontData)
      );

      setFontsCss((prev) => ({
        ...prev,
        [cacheKey]: downloadedFontCss,
      }));

      return downloadedFontCss;
    },
    [fontsCss]
  );

  /**
   * makes a request to the idleCallback queue to generate a canvas
   * for a given story page.
   *
   * @param {[string, Page]} PageTuple - a tuple containing a uid for the requested generation task & the page to generate a canvas from
   * @return {Function} a cleanup function to clear the requested canvas generation
   */
  const generateDeferredPageCanvas = useCallback(
    ([taskId, page]: [string, Page]) => {
      const cancelIdleTask = queueIdleTask({
        taskId,
        task: async () => {
          if (hasPendingPageLutAssets(page, lutContext.state)) {
            return;
          }

          try {
            const canvas = await storyPageToCanvas(
              page,
              { contexts: { lut: lutContext } },
              getFontCss
            );

            if (canvas) {
              actions.setPageCanvas({ pageId: page.id, canvas });
            }
          } catch {
            actions.setPageCanvas({ pageId: page.id, canvas: null });
          }
        },
      });
      return cancelIdleTask;
    },
    [getFontCss, queueIdleTask, actions, lutContext]
  );

  /**
   * Small wrapper around `generateDeferredPageCanvas()` that just passes in the
   * current page.
   *
   * @return {void}
   */
  const generateDeferredCurrentPageCanvas = useCallback(() => {
    const { currentPageValue, pageCanvasMapValue } = valuesRef.current;
    const canvas = pageCanvasMapValue[currentPageValue.id];
    if (typeof canvas === 'undefined') {
      generateDeferredPageCanvas([currentPageValue.id, currentPageValue]);
    }
  }, [generateDeferredPageCanvas]);

  return (
    <Context.Provider
      value={{
        state: {
          pageCanvasMap,
        },
        actions: {
          generateDeferredPageCanvas,
          generateDeferredCurrentPageCanvas,
        },
      }}
    >
      {pageIds.map((pageId) => (
        <PageCanvasCacheValidator
          key={pageId}
          pageId={pageId}
          clearPageCanvasCache={actions.clearPageCanvas}
        />
      ))}
      {children}
    </Context.Provider>
  );
}

export default PageCanvasProvider;
